import { useLayoutEffect, useState } from 'react';

/**
 * {@link useScrollbarWidth} returns the width of the scrollbar of an element found
 * through document.querySelector using a provided selector.  If the element is
 * not found, or it does not have a scrollbar, 0 is returned.
 *
 * If no selector is provided, the browser's default scrollbar width will be returned.
 *
 * @param selector The css selector to use to locate the element
 * @returns width of the scrollbar of that element, if found.
 */
export function useScrollbarWidth(selector = '#scrollbar-test'): number {
  const [width, setWidth] = useState<number>(0);
  const element = document.querySelector<HTMLElement>(selector);

  useLayoutEffect(() => {
    function checkWidth(el: HTMLElement) {
      const { offsetWidth, clientWidth } = el;
      const newWidth = offsetWidth - clientWidth;
      // Only set a width if it's changed
      if (newWidth !== width) {
        setWidth(offsetWidth - clientWidth);
      }
    }

    // Only set up a resizeObserver if we have an element to observe
    if (element) {
      // Set the value right away, without waiting for resize observer to fire,
      // so that we can get the value before the browser paints the first time.
      checkWidth(element);

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target instanceof HTMLElement && entry.target === element) {
            checkWidth(entry.target);
          }
        }
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [element, width]);

  return width;
}
